import { arrayBufferToBase64 } from "../../utils/base64";

/**
 * Turns white into transparency in place, keeping anti-aliased edges: each
 * pixel becomes the least-transparent colour that composites back to the
 * original over white. With `darkText`, near-black ink takes that colour.
 */
export function clearWhiteBackground(
  pixels: Uint8ClampedArray,
  darkText?: readonly [number, number, number],
): void {
  for (let index = 0; index < pixels.length; index += 4) {
    const coverage = Math.max(
      255 - pixels[index],
      255 - pixels[index + 1],
      255 - pixels[index + 2],
    );
    if (coverage === 0) {
      pixels[index + 3] = 0;
      continue;
    }
    for (let channel = 0; channel < 3; channel += 1) {
      pixels[index + channel] = 255 - ((255 - pixels[index + channel]) * 255) / coverage;
    }
    pixels[index + 3] = (coverage * pixels[index + 3]) / 255;
    if (!darkText) continue;
    const brightest = Math.max(pixels[index], pixels[index + 1], pixels[index + 2]);
    const dullest = Math.min(pixels[index], pixels[index + 1], pixels[index + 2]);
    if (brightest <= 0x60 && brightest - dullest <= 0x20) pixels.set(darkText, index);
  }
}

/**
 * The Preslink service paints a cell range's unfilled cells white, while an
 * Excel linked picture leaves them clear so the chart beneath shows through.
 * Over a dark chart, `darkTextColor` (RRGGBB) keeps black cell text readable.
 */
export async function toTransparentSnapshotPng(
  pngBase64: string,
  darkTextColor?: string,
): Promise<string> {
  const binary = atob(pngBase64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  const darkText = darkTextColor
    ? ([0, 2, 4].map((offset) => parseInt(darkTextColor.slice(offset, offset + 2), 16)) as [number, number, number])
    : undefined;
  const bitmap = await createImageBitmap(new Blob([bytes], { type: "image/png" }));
  try {
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas 2D is unavailable for the linked picture snapshot.");
    context.drawImage(bitmap, 0, 0);
    const image = context.getImageData(0, 0, canvas.width, canvas.height);
    clearWhiteBackground(image.data, darkText);
    context.putImageData(image, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!blob) throw new Error("Could not encode the linked picture snapshot.");
    return arrayBufferToBase64(await blob.arrayBuffer());
  } finally {
    bitmap.close();
  }
}
